import { CONTACT_TYPE, Maintainer, ORDERSERVICE_TYPE, OrderTemplate, Worker } from "@/types";
import { ConfirmModal, ContactModal, FindPersonModal, RemoveModal } from "#/organisms";
import { CreateOrderParam, UpdateOrderParam } from "@/api/order/interface";
import { Order, Progress } from "#/pages";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useContacts,
  useCustomer,
  useFields,
  useModal,
  useNotes,
  useOrderTemplate,
} from "@/hooks";
import { useNavigate, useParams } from "react-router-dom";

import { ChangePackageModal } from "@/components/organisms/Modais/ChangePackage";
import { MissingOrderInfoModal } from "@/components/organisms/Modais/MissingOrderInfo";
import { ORDER_STEP } from "@/components/pages/Order/Order/Order.interface";
import { useApi } from "@/api";

type ModalType = 'worker' | 'maintainer';

const OrderView = () => {
  const { order } = useParams<'order'>();
  const navigate = useNavigate();
  // const location = useLocation();
  const { order: orderEntity, orderTemplate: orderTemplateApi, session, worker } = useApi();

  const {
    showModal: showCancelConfirmModal,
    onCloseModal: onCloseCancelConfirmModal,
    onOpenModal: onOpenCancelConfirmModal
  } = useModal();


  const {
    showModal: showDeleteConfirmModal,
    onCloseModal: onCloseDeleteConfirmModal,
    onOpenModal: onOpenDeleteConfirmModal
  } = useModal();

  const {
    showModal: showChangePackageModal,
    onCloseModal: onCloseChangePackageModal,
    onOpenModal: onOpenChangePackageModal 
  } = useModal();

  const {
    showModal: showMissingOrderInfoModal,
    onCloseModal: onCloseMissingOrderInfoModal,
    onOpenModal: onOpenMissingOrderInfoModal 
  } = useModal();


  const isAdmin = useMemo(() => session.user?.admin || false, [session.user]);

  const [loadingOrderTemplates, setLoadingOrderTemplates] = useState(false);
  const [selectedMaintainer, setSelectedMaintainer] = useState<Maintainer | null>(null);
  const [selectedPreferredWorker, setSelectedPreferredWorker] = useState<Worker | null>(null);
  const contactsProps = useContacts();
  const { selectedCustomer } = useCustomer();
  const { contacts, onAddCustomerContact } = contactsProps;
  const { notes, onAddNote, setNotes } = useNotes();
  const [servicId, setServiceId] = useState('');
  const recordedFloorArea = useRef(0);
  const recordedVirtualTour = useRef(0);
  const noSqftSet = useRef(false);
  const noVirtualPhotosSet = useRef(false);
  
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [maintainers, setMaintainers] = useState<Maintainer[]>([]);
  const [modalType, setModalType] = useState<ModalType>('maintainer');

  const {showModal, onCloseModal, onOpenModal } = useModal();

  const {
    selectedOrderTemplate,
    setSelectedOrderTemplate
  } = useOrderTemplate();
  const {
    fieldsHaveChanges,
    storeFieldsData,
    updateFields,
    ...fields
  } = useFields();

  const getOrderDetails = async (value: string) => {
    await orderEntity.getDetailedOrderData(value);
  }

  const getOrderData = (correctDate: boolean, customerId?: string | null): CreateOrderParam => {
    const {
      addressString,
      allowFreelance,
      appointmentDate,
      appointmentDuration,
      appointmentTime,
      bedrooms,
      contactVendorDirectly,
      doorNumber,
      flat,
      floorArea,
      imageMotionRequested,
      keyNotes,
      keys,
      keysTime,
      keysTimeOption,
      keysWith,
      keysWithDetail,
      notifyViaSMS,
      orderOfferPrice,
      orderExtraChargeDescription,
      orderExtraChargePriceExVat,
      parking,
      poNumber,
      preferredWorker,
      sendNotifications,
      videoMotionRequested,
      virtualStagingRequested,
      virtualTourPartial
    } = fields;
    const dateTime = appointmentDate.value;
    const time = appointmentTime.value ? appointmentTime.value.split(':') : [];

    if (time.length > 0 && dateTime) {
      dateTime.setHours(Number(time[0]));
      dateTime.setMinutes(Number(time[1]));
    }

    if (correctDate && dateTime) {
      dateTime.setMinutes(dateTime.getMinutes() - dateTime.getTimezoneOffset());
    }

    return {
      bedrooms: Number(bedrooms.value),
      common: {
        appointmentDateTime: dateTime?.toISOString() || '',
        appointmentDuration: Number(appointmentDuration.value) || 0,
        contactVendorDirectly: contactVendorDirectly.checked,
        notifyViaSMS: notifyViaSMS.checked,
        sendNotifications: sendNotifications.checked
      },
      contacts: contacts.map((item) => ({
        contactId: item.contactId,
        customerId: item.customerId,
        email: item.email,
        id: item.contactId,
        name: item.name,
        phone1: item.phone1,
        phone2: item.phone2,
        sendInvoice: item.sendInvoice,
        type: CONTACT_TYPE.AGENT,
      })),
      customerId: customerId || '',
      floorArea: Number(floorArea.value),
      imageMotionRequested: imageMotionRequested.checked,
      keys: keys.checked,
      keysTime: keysTime.value || '',
      keysTimeOption: keysTimeOption.value,
      keysWith: keysWith.value,
      keysWithDetail: keysWithDetail.value,
      notes: keyNotes.value,
      orderExtraChargeDescription: orderExtraChargeDescription.value,
      orderExtraChargePriceExVat: Number(orderExtraChargePriceExVat.value),
      orderNotes: notes.map((item) => ({ content: item.content })),
      orderOfferPrice: Number(orderOfferPrice.value),
      orderReference: '',
      parking: parking.value,
      poNumber: poNumber.value,
      preferredWorker: preferredWorker.value,
      propertyAddress: {
        addressString: addressString.value,
        doorNumber: doorNumber.value,
        flat: flat.value,
      },
      useOrderOffer: allowFreelance.checked,
      videoMotionRequested: videoMotionRequested.checked,
      virtualStagingRequested: virtualStagingRequested.checked,
      virtualTourPartial: virtualTourPartial.checked,
    }
  }

  const getUpdatedOrderData = (orderIdToUpdate: string): UpdateOrderParam => {
    const { orderState } = fields;

    return {
      ...getOrderData(true, selectedCustomer?.id ),
      maintainer: {
        applicationUserId: selectedMaintainer?.id || '',
        firstName: selectedMaintainer?.firstName || '',
        id: selectedMaintainer?.id || '',
        lastName: selectedMaintainer?.lastName || '',
      },
      orderId: orderIdToUpdate,
      orderState: Number(orderState.value),
      services: orderEntity.orderDetails?.services || [],
    }
  }

  const updateOrder = async () => {
    if (!order) {
      return;
    }
    const orderData = getUpdatedOrderData(order);

    await orderEntity.setDetailedOrderData(orderData);
    storeFieldsData(orderData)
  }

  // EDIT PAGE
  useEffect(() => {
    if (order) {
      getOrderDetails(order);
    }
  }, [order]);

  useEffect(() => {
    const { orderDetails } = orderEntity;

    if (orderDetails) {
      const { customerId, description, orderPrice, services } = orderDetails;

      updateFields();
      storeFieldsData(getOrderData(false, customerId));

      contactsProps.setContacts(orderEntity.orderDetails?.contacts.map(c => { return { 
        contactId: c.id,
        customerId: orderEntity.orderDetails?.customerId || '',
        email: c.email,
        name: c.name,  
        phone1: c.phone1,
        phone2: c.phone2 || '',
        sendInvoice: false,
      }}) || []);

      // if (orderDetails.contacts.length > 0) {
      //   contactsProps.setContacts(orderDetails.contacts.map((item) => ({
      //     contactId: `${item.id}`,
      //     customerId: '',
      //     email: item.email,
      //     name: item.name,
      //     phone1: item.phone1 || '',
      //     phone2: item.phone2 || '',
      //     type: item.type,
      //   })));
      // }

      if (orderDetails.orderNotes && orderDetails.orderNotes.length > 0) {
        setNotes(orderDetails.orderNotes);
      }
      else {
        setNotes([]);
      }

      //THIS IS BS
      setSelectedOrderTemplate({
        customerId,
        description,
        id: Number(orderDetails.id),
        isVirtual: orderDetails.isVirtual,
        price: orderPrice,
        services: services.map((item) => ({
          defaultAmount: 0,
          imageUrl: item.imageURL,
          name: item.name,
          orderTypeId: item.orderServiceId,
          price: item.price,
        })),
      })
    }
  }, [orderEntity.orderDetails]);

  const handleCancelOrder = useCallback(() => {
    if (orderEntity.orderDetails?.id) {
      orderEntity.deleteOrder(orderEntity.orderDetails.id);
      navigate('/orders-unmaintained');
    }
  }, [orderEntity.orderDetails]);

  const handleDeleteOrder = useCallback(() => {
    if (orderEntity.orderDetails?.id) {
      orderEntity.deleteOrder(orderEntity.orderDetails.id);
      navigate('/orders-unmaintained');
    }
  }, [orderEntity.orderDetails]);

  const handleMarkForInvoicing = async () => {
    if (orderEntity.orderDetails?.id) {
      orderEntity.markForInvoicing(orderEntity.orderDetails.id);
    }
  }

  const handleSaveOrder = async () => {
    orderEntity.orderDetails?.services.forEach(s => {
      if (s.type === ORDERSERVICE_TYPE.FLOORPLAN && s.floorArea === 0 && recordedFloorArea.current === 0) {
        noSqftSet.current = true;
      }

      if (s.type === ORDERSERVICE_TYPE.VIRTUAL_TOUR && s.virtualTourPhotos === 0 && recordedVirtualTour.current === 0) {
        noVirtualPhotosSet.current = true;
      }
    });

    if (noSqftSet.current || noVirtualPhotosSet.current) {
      onOpenMissingOrderInfoModal();
    } else {
      await updateOrder();
    }
  };

  const handleSaveWithMissingInfo = async () => {
    await updateOrder();
    onCloseMissingOrderInfoModal();
  }

  const handleChangePackage = async() => {
    setLoadingOrderTemplates(true);
    onOpenChangePackageModal();
    orderTemplateApi.getList([ { BranchId: orderEntity.orderDetails?.branchId || 0, Name: 'No matter',  Selected: false }]);
    setLoadingOrderTemplates(false);
  }

  const handleSelectOrderTemplate = async (orderTemplate: OrderTemplate) => {
      await orderEntity.changeTemplate(orderEntity.orderDetails?.id || '', orderTemplate.id);
      await orderEntity.getDetailedOrderData(orderEntity.orderDetails?.id || '');
  }

  const handleAddCustomerContact = useCallback(() => {
    if (selectedCustomer) {
      onAddCustomerContact(selectedCustomer.branchId ?? 0, selectedCustomer.id)
    }
  }, [selectedCustomer]);

  const handleNotifyCustomer = useCallback((serviceId: string) => {
      orderEntity.notifyCustomer(serviceId);
  }, [orderEntity.orderDetails]);

  const handleOnTakeRole = useCallback(() => {
    const { orderDetails } = orderEntity;

    if (orderDetails) {
      setSelectedMaintainer({
        email: session.user?.email || '',
        firstName: session.user?.firstName || '',
        id: session.user?.id || '',
        lastName: session.user?.lastName || ''
      });
    }
  }, [orderEntity.orderDetails]);

  const handleUploadFiles = useCallback(async (serviceId: string, files: File[], isDraft: boolean, isOffered?: boolean) => {
    if (order) {
      if (isOffered) {
        await worker.uploadFileOffered(files, serviceId);
      } else {
      await worker.uploadPhotos(files, serviceId, isDraft);
      }

      return getOrderDetails(order);
    }
  }, [order]);

  const handleConfirmFiles = useCallback(async () => {
    if (order) {
      return getOrderDetails(order);
    }
  }, [order]);

  const handleDeleteFiles = useCallback(async (serviceId: string, files: string[], isOffered: boolean) => {
    if (order) {
      await worker.deletePhotos(files, serviceId, isOffered);

      return getOrderDetails(order);
    }
  }, [order]);

  const handleMoveFiles = useCallback(async (serviceId: string, files: string[], isOffered: boolean) => {
    if (order) {
      await worker.movePhotos(serviceId, files, isOffered);

      return getOrderDetails(order);
    }
  }, [order]);

  const handleDownloadFiles = useCallback((serviceId: string, filesId: string[], isDraft: boolean) => {
    if (order) {
      orderEntity.downloadOrderFiles(order, serviceId, [{ name: 'fullSize' }], filesId, isDraft);
    }
  }, [order]);

  const handleGetFloorArea = useCallback((serviceId: string) => {
    return worker.getFloorArea(serviceId);
  }, []);

  const handleRecordFloorArea = useCallback((serviceId: string, floorArea: number) => {
    recordedFloorArea.current = floorArea;

    return worker.recordFloorArea(serviceId, floorArea);
  }, [recordedFloorArea.current]);

  const handleRecordVirtualTour = useCallback((serviceId: string, virtualTourPhotos: number) => {
    recordedVirtualTour.current = virtualTourPhotos;

    return worker.recordVirtualTour(serviceId, virtualTourPhotos);
  }, [recordedVirtualTour.current]);

  const handleSelectWorker = useCallback((serviceId: string, seldWorker: Worker) => {
    if (!orderEntity.orderDetails)
      return;
    orderEntity.orderDetails.services = orderEntity.orderDetails.services.map((item) => {
      return (item.orderServiceId === serviceId || serviceId === '' )
      ? {...item, applicationUserId: seldWorker.id, applicationUserName: seldWorker.name}
      : item
    });
  }, [orderEntity.orderDetails]);

  const handleSelect = useCallback((id: string) => {

    if (modalType === 'maintainer') {
      const selected = maintainers.find((item) => item.id === id);

      if (selected) {
        setSelectedMaintainer(selected)
      }
    } else {
      const selected = workers.find((item) => item.id === id);

      if (selected) {
        setSelectedPreferredWorker(selected);
        handleSelectWorker(servicId, selected);
      }
    }
  }, [maintainers, workers, modalType, servicId]);

  const handleChangeRole = useCallback(() => {
    setModalType('maintainer');
    onOpenModal();
  }, []);

  const handleChangeWorker = useCallback((serviceIdInput: string) => {
    setModalType('worker');
    setServiceId(serviceIdInput);
    onOpenModal();
  }, []);

  const handleFindPerson = async (search: string) => {
    if (modalType === 'maintainer') {
      const res = await orderEntity.findMaintainer(search);
      setMaintainers(res);
    } else {
      const res = await orderEntity.findWorker(search);
      setWorkers(res);
    }
  };

  const handleChangeLabel = useCallback((value: string, serviceId: string) => {
    if (!orderEntity.orderDetails)
      return;
    orderEntity.orderDetails.services = orderEntity.orderDetails.services.map((item) => {
      return (item.orderServiceId === serviceId || serviceId === '' )
      ? {...item, label: value}
      : item
    });
    orderEntity.updateOrderDetails(orderEntity.orderDetails);
  }, [orderEntity.orderDetails]);

  const handleChangeUrl = useCallback((value: string, serviceId: string) => {
    if (!orderEntity.orderDetails)
      return;
    orderEntity.orderDetails.services = orderEntity.orderDetails.services.map((item) => {
      return (item.orderServiceId === serviceId || serviceId === '' )
      ? {...item, externalLink: value}
      : item
    });
    orderEntity.updateOrderDetails(orderEntity.orderDetails);
  }, [orderEntity.orderDetails]);

  const handleNavigateToCustomer = () => {
    navigate(`/order-view/order=${order}`);
  }

  const handleNavigateToWorker = () => {
    navigate(`/order-view-worker/order=${order}`);
  }

  return (
    <>
      <Order
        activeStep={ORDER_STEP.PROGRESS}
        invoiceOverride={orderEntity.orderDetails?.invoiceOverride || false}
        isAdmin={isAdmin}
        selectedOrder={selectedOrderTemplate}
        onCancelOrder={onOpenCancelConfirmModal}
        onDeleteOrder={onOpenDeleteConfirmModal}
        onMarkForInvoicing={handleMarkForInvoicing}
        onSaveOrder={handleSaveOrder}
      >
        {orderEntity.orderDetails && <Progress
            {...contactsProps}
            {...fields}
            isAdmin={isAdmin}
            notes={notes}
            coordinator={selectedMaintainer}
            customerName={orderEntity.orderDetails.companyName}
            customerUserId={orderEntity.orderDetails.customerUserId} 
            description={orderEntity.orderDetails.description}
            hasInvoice={orderEntity.orderDetails.hasInvoice}
            invoiceLink={orderEntity.orderDetails.invoiceLink}
            logoUrl={''}
            offeredServices={orderEntity.orderDetails.offeredServices}
            onAddCustomerContact={handleAddCustomerContact}
            onAddNote={onAddNote}
            onChangeLabel={handleChangeLabel}
            onChangeUrl={handleChangeUrl}
            onChangePackage={handleChangePackage}
            onConfirmFiles={handleConfirmFiles}
            onDeleteFiles={handleDeleteFiles}
            onDownloadFiles={handleDownloadFiles}
            onGetFloorArea={handleGetFloorArea}
            onMoveFiles={handleMoveFiles}
            onNavigateToCustomer={handleNavigateToCustomer}
            onNavigateToWorker={handleNavigateToWorker}
            onRecordFloorArea={handleRecordFloorArea}
            onRecordVirtualTour={handleRecordVirtualTour}
            onNotifyCustomer={handleNotifyCustomer}
            onTakeRole={handleOnTakeRole}
            onUploadFiles={handleUploadFiles}
            onChangeRole={handleChangeRole}
            onAskToChangeWorker={handleChangeWorker}
            orderId={orderEntity.orderDetails.id}
            preferredWorker={selectedPreferredWorker}
            selectedOrderTemplate={selectedOrderTemplate}
            selectedMaintainer={selectedMaintainer}
            services={orderEntity.orderDetails.services}
            owner= {`${session.user?.firstName} ${session.user?.lastName}`}
          /> }
      </Order>
      {showModal && (
        <FindPersonModal
          onFindPerson={handleFindPerson}
          onSelectPerson={handleSelect}
          persons={modalType === 'maintainer'
            ? maintainers.map((item) => ({
              email: item.email,
              id: item.id,
              name: `${item.firstName} ${item.lastName}`,
            }))
            : workers.map((item) => ({
              email: item.email,
              id: item.id,
              name: item.name,
            }))
          }
          onClose={onCloseModal}
        />
      )}
      {showCancelConfirmModal && (
        <ConfirmModal
          description="Canceling the order will deactivate the order and charge the client a fee. Are you sure?"
          onClose={onCloseCancelConfirmModal}
          onConfirm={handleCancelOrder}
          title="Cancel Order"
        />
      )}
      {showDeleteConfirmModal && (
        <ConfirmModal
          description="Are you sure you want to delete order?"
          onClose={onCloseDeleteConfirmModal}
          onConfirm={handleDeleteOrder}
          title="Delete Order"
        />
      )}
      {showChangePackageModal && (
        <ChangePackageModal
          isAdmin={isAdmin}
          loading={loadingOrderTemplates}
          onClose={onCloseChangePackageModal}
          onSelectOrderTemplate={handleSelectOrderTemplate}
          orderTemplates={orderTemplateApi.orderTemplates}
        />) }
      {contactsProps.showContactModal && (
        <ContactModal
          contact={contactsProps.contactToEdit}
          onAddNew={contactsProps.onAddContact}
          onEdit={contactsProps.onEditContact}
          onClose={contactsProps.onCloseContactModal}
          type={contactsProps.modalType}
          />
        )}
        {contactsProps.showRemoveModal && (
            <RemoveModal
                description="Are you sure you want to remove this contact?"
                onCancel={contactsProps.onCloseRemoveModal}
                onClose={contactsProps.onCloseRemoveModal}
                onRemove={contactsProps.onRemoveContact}
                title="Remove Contact"
            />
        )}
        {showMissingOrderInfoModal && (
          <MissingOrderInfoModal
            floorAreaMissing={noSqftSet.current}
            virtualTourPhotosMissing={noVirtualPhotosSet.current}
            onClose={onCloseMissingOrderInfoModal}
            onConfirm={handleSaveWithMissingInfo}
          />) }
    </>
  );
}

export default OrderView;
